/* eslint-disable no-useless-escape */
/**
 * Add validation to an input element. It will add a green success or yellow
 * warning mark inside the put plus a validation error message depending on the
 * input status.
 *
 * @param {HTMLElement} el input to validate
 * @param {string} validationRule one of required, email, phone number.
 * @param {string} errorMessage error to show when validation fails. If no error is specified,
 *                              a data-validation-error element will be searched on el and used.
 * @callback onValidationCallback to be called after the validation gets performed. Receives the
 *                                validation DOMel and validation status
 */
 const validateFormElement = (el, validationRule, errorMessage = null,
  onValidationCallback = null, ctaEl) => {
    
    /** Checking if @type {HTMLElement} is a checkbox so we can give a different UI validation. */
    const isCheckbox = el.type === 'checkbox';
    const checkboxUiValidation = () => {
      const label = isCheckbox && el.parentElement.querySelector('.afd-checkbox__label')
      isCheckbox && el.checked
        ? label.classList.remove('kth-error-label')
        : label.classList.add('kth-error-label')
    }

    // list of valid validation rules the user can use
    const validationRules = {
      // required means: more than zero characters, can't be just whitespaces
      required: () => !el.value?.length || /^\s*$/.test(el.value),
      email: () => !el.value?.length || !(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(el.value)),
      phone: () => el.value.length <= 10 || el.value.length >= 17 || !(/^[+]?[\s./0-9]*[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/g.test(el.value)),
      checkbox: () => !el.checked,
      link: () => !el.value?.length || !el.checkValidity()
    };
    
  const findValidationElements = () => {
    let validationAlert = null;
    let validationErrorMessage = null;

    if (!isCheckbox) {
      // clean sibling class from previous validations
      if (el.nextElementSibling !== null && el.nextElementSibling.classList.contains('kth-input-val')) {
        validationAlert = el.nextElementSibling;
        validationAlert.classList.remove('kth-input-val--true');
        validationAlert.classList.remove('kth-input-val--false');
        // if we have the error message element, then we assign the right DOM element
        if (validationAlert.nextElementSibling.classList.contains('kth-form__hint')) {
          validationErrorMessage = validationAlert.nextElementSibling;
        }
      } else {
        // we will need an element to put the alert
        validationAlert = document.createElement('div');
        validationAlert.classList.add('kth-input-val');
        // and also an element to put the error message
        validationErrorMessage = document.createElement('div');
        validationErrorMessage.classList.add('kth-input-tip', 'kth-form__hint');
      }
    } else {
      checkboxUiValidation()
    }
    return { validationAlert, validationErrorMessage };
  };

  /**
   * Updates the UI (error/success messages and CTA status) after a validation is made on an
   * element of the form being validated.
   * @param {Object} validationElements object with HTMLElements to update to show error/success
   *                                    messages
   * @param {boolean} validationStatus true if validation is ok, false in the opposite case
   * @param {boolean} skipUI [false] true if you want avoid update validationElements, false in
   *                                 the opposite case. Useful to just update the CTA status.
   */
  const actOnValidation = (validationElements, validationStatus, skipUI = false) => {
    const { validationAlert, validationErrorMessage } = validationElements;
    if (!skipUI && !isCheckbox) {
      if (validationStatus === false) {
        // case when validation fails
        validationAlert.classList.add('kth-input-val--false');
        validationErrorMessage.classList.remove('afd-hide');
        if (errorMessage === null) {
          validationErrorMessage.textContent = el.dataset.validationError;
        } else {
          validationErrorMessage.textContent = errorMessage;
        }
      } else {
        // case when validation is successful
        validationAlert.classList.add('kth-input-val--true');
        validationErrorMessage.classList.add('afd-hide');
      }
    }
    // If is a Checkbox (Terms & Conditions)
    isCheckbox && checkboxUiValidation()

    // if we have callback, we use it
    if (onValidationCallback) {
      onValidationCallback(el, validationStatus);
    }
  };

  const updateValidationUI = (validationElements) => {
    const { validationAlert, validationErrorMessage } = validationElements;
    // insert validation elements if they are not already there
    if (!document.body.contains(validationAlert) && !isCheckbox) {
      el.after(validationAlert);
      validationAlert.after(validationErrorMessage);
    } 
    // If is a Checkbox (Terms & Conditions)
    isCheckbox && checkboxUiValidation()
  };

  // attach listeners to perform validation
  // on blur, we perform a full validation
  el.addEventListener('blur', () => {
    const validationElements = findValidationElements();
    // now, perform validation
    actOnValidation(validationElements, !validationRules[validationRule]());
    updateValidationUI(validationElements);
  });

  // on input, we just signal a validation that has passed. We don't update UI with
  // errors on input because it's considered an UX practice that can lower conversion
  // rates (see http://alistapart.com/article/inline-validation-in-web-forms).
  el.addEventListener('input', () => {
    const validationElements = findValidationElements(el);
    if (!validationRules[validationRule]()) {
      actOnValidation(validationElements, true); // force a success validation
      updateValidationUI(validationElements);
    } else {
      actOnValidation(validationElements, false, true); // force a CTA update
    }
  });
  
  ctaEl.addEventListener('click', () => {
    const validationElements = findValidationElements();
    actOnValidation(validationElements, !validationRules[validationRule]());
    updateValidationUI(validationElements);
  })

  // Trigger click event on "Enter" key press
  el.addEventListener('keyup', (event) => {
    // Execute click event only if is not a Message Section
    if (event.keyCode === 13 && el.id !== 'verification_request_request_message') 
      ctaEl.click()
  })
};



/**
 * Validates the given form. You need to specify by hand which elements of the form to validate
 * and also the rules to use.
 *
 * @param {HTMLElement} formEl form to validate
 * @param {Object} options two attributes required: validations (object of elements to validate,
 *                         key is attr selector and value is rule to use) and cta (selector of CTA).
 */
const validateForm = (formEl, options) => {
  const internalValidator = {};

  // logic to enable/disable the CTA button of the form
  const ctaEl = formEl && formEl.querySelector(options.cta);
  /** @type {onValidationCallback} */
  const checkForm = (attr, validationStatus) => {
    
    internalValidator[attr.id] = validationStatus;
    const formStatus = Object.values(internalValidator).reduce((t, status) => t && status, true);
    if (formStatus) {
      ctaEl.classList.remove('afd-btn--not-allowed');
    } else {
      ctaEl.classList.add('afd-btn--not-allowed');
    }
  };

  Object.entries(options.validations).forEach(([attr, rule]) => {
    const attrEl = formEl && formEl.querySelector(attr);
    if (attrEl) {
      internalValidator[attrEl.id] = false; // by default, is invalid
      validateFormElement(attrEl, rule, null, checkForm, ctaEl);
    }
  });


};

export { validateForm };